import React, { useState, useEffect, useMemo, useRef } from 'react';
import { UserHorseNoteRead, UserHorseNoteCreate, NoteType } from '../types/api';
import { apiService } from '../services/api';
import {
  Plus,
  X
} from 'lucide-react';

interface HorseNotesProps {
  horseId: string;
  horseName: string;
  initialShowForm?: boolean;
  isEditing?: boolean;
  onFormVisibilityChange?: (isVisible: boolean) => void;
  initialNotes?: UserHorseNoteRead[];
}

const allowedNoteTypes: NoteType[] = ['text'];

export const HorseNotes: React.FC<HorseNotesProps> = ({ horseId, horseName, initialShowForm = false, isEditing = false, onFormVisibilityChange, initialNotes }) => {
  const [notes, setNotes] = useState<UserHorseNoteRead[]>(() =>
    initialNotes ? initialNotes.filter(n => allowedNoteTypes.includes(n.note_type)) : []
  );
  const [loading, setLoading] = useState(!initialNotes);
  const [adding, setAdding] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const addInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [filterType] = useState<NoteType | 'all'>('all');

  useEffect(() => {
    setAdding(initialShowForm);
    onFormVisibilityChange?.(!!initialShowForm);
  }, [initialShowForm, onFormVisibilityChange]);

  useEffect(() => {
    if (!initialNotes) {
      loadNotes();
    }
  }, [horseId, filterType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (adding && addInputRef.current) {
      addInputRef.current.focus();
    }
  }, [adding]);

  const loadNotes = async () => {
    setLoading(true);
    try {
      const noteTypeFilter = filterType === 'all' ? undefined : filterType;
      const notesData = await apiService.getHorseNotes(horseId, noteTypeFilter);
      const filtered = notesData.filter(note => allowedNoteTypes.includes(note.note_type));
      setNotes(filtered);
    } catch (err) {
      // Suppress errors - just show empty notes
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async (noteData: UserHorseNoteCreate) => {
    try {
      const newNote = await apiService.createHorseNote(horseId, noteData);
      setNotes(prev => [newNote, ...prev]);
      setAdding(false);
      setNewNoteContent('');
      setNoteDrafts(prev => ({ ...prev, [newNote.id]: newNote.content }));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create note');
    }
  };

  const handleUpdateNote = async (noteId: string, noteData: Partial<UserHorseNoteCreate>) => {
    try {
      const updatedNote = await apiService.updateHorseNote(horseId, noteId, noteData);
      setNotes(prev => prev.map(note => note.id === noteId ? updatedNote : note));
      setNoteDrafts(prev => ({ ...prev, [noteId]: updatedNote.content }));
      setAdding(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await apiService.deleteHorseNote(horseId, noteId);
      setNotes(prev => prev.filter(note => note.id !== noteId));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete note');
    }
  };

  useEffect(() => {
    // keep drafts in sync when notes change
    setNoteDrafts((prev) =>
      notes.reduce((acc, n) => {
        acc[n.id] = prev[n.id] ?? n.content;
        return acc;
      }, {} as Record<string, string>)
    );
  }, [notes]);

  const filteredNotes = useMemo(() => notes, [notes]);

  const saveDraft = async (noteId: string) => {
    const draft = noteDrafts[noteId] ?? '';
    if (!draft.trim()) return;
    await handleUpdateNote(noteId, { content: draft });
  };
  const formatDate = (iso: string) => new Date(iso).toLocaleDateString();

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#233454]">
        <Plus size={16} className="text-[#233454]" />
        <span>Loading notes...</span>
      </div>
    );
  }

  // No notes - show add button or editable placeholder in edit mode
  if (filteredNotes.length === 0) {
    if (isEditing) {
      return (
        <div className="flex items-start gap-2">
          <span className="text-sm font-semibold text-[#233454] w-[56px] shrink-0">Notes:</span>
          <div className="flex-1 space-y-2">
            <div className="flex items-start">
              {adding ? (
                <div className="space-y-1 w-full">
                  <textarea
                    value={newNoteContent}
                    onChange={(e) => setNewNoteContent(e.target.value)}
                    onBlur={async () => {
                      if (newNoteContent.trim()) {
                        await handleCreateNote({ title: newNoteContent.trim(), note_type: 'text', content: newNoteContent.trim() });
                      }
                      setAdding(false);
                    }}
                    className="w-full bg-[#ebf6e4] rounded-[5px] px-3 py-2 text-sm text-[#233454] border border-transparent focus:border-[#9fbe90] focus:outline-none resize-none"
                    rows={Math.max(2, Math.ceil(newNoteContent.length / 40))}
                    placeholder="Add notes"
                  />
                </div>
              ) : (
                <button
                  className="bg-[#f2fff3] h-[32px] px-[10px] py-[6px] rounded-[5px] text-sm text-[#233454] hover:bg-[#e5f5e7] transition-colors inline-flex items-center"
                  onClick={() => {
                    setAdding(true);
                    setNewNoteContent('');
                  }}
                >
                  +
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="space-y-2">
      {isEditing ? (
        /* Edit Mode - Notes: editable list with delete X */
        <div className="flex items-start gap-2">
          <span className="text-sm font-semibold text-[#233454] w-[56px] shrink-0 mt-0.5">Notes:</span>
          <div className="flex-1 min-w-0 space-y-2">
            {/* Add control at top */}
            {adding ? (
              <div className="space-y-1">
                <textarea
                  ref={addInputRef}
                  value={newNoteContent}
                  onChange={(e) => setNewNoteContent(e.target.value)}
                  onBlur={async () => {
                    if (newNoteContent.trim()) {
                      await handleCreateNote({ title: newNoteContent.trim(), note_type: 'text', content: newNoteContent.trim() });
                    }
                    setAdding(false);
                  }}
                  className="w-full bg-[#ebf6e4] rounded-[5px] px-3 py-2 text-sm text-[#233454] border border-transparent focus:border-[#9fbe90] focus:outline-none resize-none"
                  rows={Math.max(2, Math.ceil(newNoteContent.length / 40))}
                  placeholder="Add a note..."
                />
              </div>
            ) : (
              <div className="flex">
                <button
                  className="bg-[#f2fff3] h-[32px] w-[32px] rounded-[5px] text-sm text-[#233454] hover:bg-[#e5f5e7] transition-colors inline-flex items-center justify-center"
                  onClick={() => {
                    setAdding(true);
                    setNewNoteContent('');
                  }}
                >
                  +
                </button>
              </div>
            )}

            {/* Existing notes */}
            {filteredNotes.map((note) => (
              <div
                key={note.id}
                className="bg-[#ebf6e4] rounded-[5px] px-3 py-2 text-sm text-[#233454] w-full"
              >
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="text-[#233454] hover:text-red-600 transition-colors"
                    aria-label="Delete note"
                    onClick={() => handleDeleteNote(note.id)}
                  >
                    <X size={14} />
                  </button>
                </div>
                <textarea
                  value={noteDrafts[note.id] ?? note.content}
                  onChange={(e) =>
                    setNoteDrafts((prev) => ({ ...prev, [note.id]: e.target.value }))
                  }
                  onBlur={() => saveDraft(note.id)}
                  className="w-full bg-transparent text-sm text-[#233454] whitespace-pre-wrap break-words text-left resize-none outline-none border border-transparent focus:border-[#9fbe90] rounded-[5px] px-2 py-1"
                  rows={Math.max(2, Math.ceil((noteDrafts[note.id]?.length || note.content.length) / 40))}
                />
                <p className="text-[11px] text-[#233454] italic mt-1 text-right">Created: {formatDate(note.created_at)}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* View Mode */
        <div className="flex items-start gap-2">
          <span className="text-sm font-semibold text-[#233454] w-[56px] shrink-0 mt-0.5">Notes:</span>
          <div className="flex-1 min-w-0 space-y-2">
            {filteredNotes.map((note) => (
              <div
                key={note.id}
                className="bg-[#ebf6e4] rounded-[5px] px-3 py-2 text-sm text-[#233454] w-full"
              >
                <p className="whitespace-pre-wrap break-words text-left">{note.content}</p>
                <p className="text-[11px] text-[#233454] italic mt-1 text-right">Created: {formatDate(note.created_at)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// NoteCard removed (view mode now inline)
